import { useEffect, useRef, useState } from "react";
import { Heart, MessageCircle, ShoppingBag } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { rp, thumb, stockLabel, stockColor } from "@/lib/format";
import { DiscountBadge, UrgencyChip, HematChip } from "@/components/PromoChips";
import ChatAdminSheet from "@/components/ChatAdminSheet";
import T from "@/lib/testIds";

/**
 * ProductCard v2 — REBUILD FROM ZERO.
 * Direction: "dreamy card-in-frame" — BUKAN doomscroll hitam ala TikTok.
 * Base theme zunayya: cream bg, plum text, sakura accents.
 *
 * Layout per layar:
 *   1. Foto dalam frame rounded besar (margin dari tepi, shadow dreamy)
 *   2. Badge + like melayang di atas foto
 *   3. Panel info di bawah foto (bg cream, teks plum)
 *   4. Dock aksi melayang: Like / Beli / Chat
 *
 * Micro-interactions: ken-burns foto, double-tap big heart, content fade-up.
 */
export default function ProductCard({ product, onOpenIntent }) {
  const [liked, setLiked] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jt_likes") || "{}")[product.id] || false; } catch { return false; }
  });
  const [likeCount, setLikeCount] = useState(product.like_count || 0);
  const [imgIdx, setImgIdx] = useState(0);
  const [burst, setBurst] = useState(false);
  const [showBigHeart, setShowBigHeart] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const rootRef = useRef(null);
  const viewFired = useRef(false);
  const tapTimer = useRef(null);

  const photos = product.foto_urls?.length ? product.foto_urls : [];
  const soldOut = product.stock_status === "sold_out";

  /* ── View tracking ── */
  useEffect(() => {
    if (!rootRef.current || viewFired.current) return;
    let timer;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.6) {
            timer = setTimeout(() => {
              if (viewFired.current) return;
              viewFired.current = true;
              api.post("/events", { product_id: product.id, type: "view" }).catch(() => {});
            }, 900);
          } else if (timer) clearTimeout(timer);
        });
      },
      { threshold: [0, 0.6, 1] }
    );
    io.observe(rootRef.current);
    return () => { if (timer) clearTimeout(timer); io.disconnect(); };
  }, [product.id]);

  /* ── Like ── */
  const persistLike = (val) => {
    try {
      const store = JSON.parse(localStorage.getItem("jt_likes") || "{}");
      if (val) store[product.id] = true; else delete store[product.id];
      localStorage.setItem("jt_likes", JSON.stringify(store));
    } catch {}
  };
  const fireBurst = () => {
    setBurst(true);
    setTimeout(() => setBurst(false), 700);
    try { navigator.vibrate?.(10); } catch {}
  };
  const addLike = () => {
    setLiked(true); setLikeCount((c) => c + 1); persistLike(true); fireBurst();
    api.post("/events", { product_id: product.id, type: "like" }).catch(() => {});
  };
  const removeLike = () => {
    setLiked(false); setLikeCount((c) => Math.max(0, c - 1)); persistLike(false);
    api.post("/events", { product_id: product.id, type: "unlike" }).catch(() => {});
  };
  const toggleLike = () => (liked ? removeLike() : addLike());

  const onImageTap = () => {
    if (tapTimer.current) {
      clearTimeout(tapTimer.current);
      tapTimer.current = null;
      setShowBigHeart(true);
      setTimeout(() => setShowBigHeart(false), 850);
      if (!liked) addLike(); else fireBurst();
    } else {
      tapTimer.current = setTimeout(() => { tapTimer.current = null; }, 260);
    }
  };

  /* ── Intent beli ── */
  const doIntent = async () => {
    if (soldOut) { toast.error("Barang sudah habis 😢"); return; }
    try { await api.post("/events", { product_id: product.id, type: "intent" }); } catch (e) {
      if (e?.response?.status === 400) { toast.error("Barang sudah habis"); return; }
    }
    const prefersReduced = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!prefersReduced) {
      confetti({ particleCount: 80, spread: 70, origin: { y: 0.7 }, colors: ["#F5A9C7", "#FFDEB0", "#C8B4E4"] });
    }
    onOpenIntent?.(product);
  };

  const DOCK_H = 52;

  return (
    <div
      ref={rootRef}
      data-testid={T.productCard(product.id)}
      className="relative h-full w-full overflow-hidden bg-cream dark:bg-plum-700"
    >
      {/* Dekorasi ring tipis ala grid hero — tema dreamy */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-24 -right-10 w-44 h-44 rounded-full border border-sakura-300/30" />
        <div className="absolute top-44 right-6 w-24 h-24 rounded-full border border-lilac-300/25" />
        <div className="absolute bottom-40 -left-12 w-52 h-52 rounded-full border border-babyblue-300/20" />
      </div>

      <div className="relative h-full flex flex-col px-4 pt-[calc(env(safe-area-inset-top)+76px)] pb-[calc(20px+env(safe-area-inset-bottom))]">

        {/* ── 1. FOTO FRAME ── */}
        <div
          className="relative flex-1 min-h-0 rounded-[28px] overflow-hidden border border-plum/10 shadow-dreamy bg-plum/5"
          onClick={onImageTap}
        >
          {photos.length > 0 ? (
            <img
              src={thumb(photos[imgIdx], 1000)}
              alt={product.nama}
              loading="lazy"
              className="h-full w-full object-cover animate-ken-burns"
              draggable={false}
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-sakura-200 via-lilac-200 to-plum-400 grid place-items-center">
              <span className="font-serif italic text-4xl text-white/80">zunayya</span>
            </div>
          )}

          {/* Multi-photo nav */}
          {photos.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setImgIdx((i) => (i - 1 + photos.length) % photos.length); }} className="absolute left-0 top-0 h-full w-1/3" aria-label="prev photo" />
              <button onClick={(e) => { e.stopPropagation(); setImgIdx((i) => (i + 1) % photos.length); }} className="absolute right-0 top-0 h-full w-1/3" aria-label="next photo" />
              <div className="absolute top-3 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                {photos.map((_, i) => (
                  <span key={i} className={`h-1 rounded-full transition-all ${i === imgIdx ? "w-6 bg-white" : "w-3 bg-white/50"}`} />
                ))}
              </div>
            </>
          )}

          {/* Badges kiri atas */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start z-10">
            {product.stock_status !== "preorder" && (
              <span className={`px-2.5 py-1 rounded-full text-[9px] font-semibold uppercase tracking-[0.18em] border border-white/60 backdrop-blur-sm ${stockColor(product.stock_status)}`}>
                {stockLabel(product.stock_status)}
              </span>
            )}
            <DiscountBadge persen={product.diskon_persen} />
            <UrgencyChip soldOut={soldOut} diskon={product.diskon_persen || 0} stockStatus={product.stock_status} />
          </div>

          {/* Big heart overlay (double-tap) */}
          {showBigHeart && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none z-30">
              <Heart className="h-24 w-24 fill-sakura-400 text-sakura-300 animate-big-heart drop-shadow-[0_6px_24px_rgba(245,169,199,0.8)]" />
            </div>
          )}

          {/* Sold out veil */}
          {soldOut && (
            <div className="absolute inset-0 bg-plum/45 grid place-items-center z-20">
              <span className="px-6 py-2.5 rounded-full bg-cream/95 text-plum text-xs font-bold uppercase tracking-[0.3em]">Habis</span>
            </div>
          )}
        </div>

        {/* ── 2. PANEL INFO (cream, plum text) ── */}
        <div className="pt-4 pb-3 animate-feed-content">
          <p className="text-[9px] font-medium uppercase tracking-[0.25em] text-plum/45 dark:text-cream/50 mb-1">{product.kategori}</p>
          <h2 className="font-serif italic text-[1.7rem] leading-[1.08] text-plum dark:text-cream line-clamp-2 mb-2">{product.nama}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-serif italic text-2xl font-semibold text-sakura-600 dark:text-sakura-300">{rp(product.harga_idr)}</span>
            {product.harga_coret_idr > product.harga_idr && (
              <span className="text-sm text-plum/40 dark:text-cream/40 line-through">{rp(product.harga_coret_idr)}</span>
            )}
            <HematChip hargaCoret={product.harga_coret_idr} hargaJual={product.harga_idr} />
          </div>
        </div>

        {/* ── 3. DOCK ── */}
        <div className="flex items-center justify-center" style={{ gap: "12px" }}>
          {/* Like */}
          <button onClick={toggleLike} data-testid={T.productLike(product.id)} aria-label="Like" className="transition-transform active:scale-90">
            <span
              className={`relative grid place-items-center rounded-full bg-white dark:bg-plum/40 border border-plum/10 shadow-dreamy ${burst ? "animate-heart-burst" : ""}`}
              style={{ height: `${DOCK_H}px`, width: `${DOCK_H}px` }}
            >
              <Heart className={`h-5 w-5 ${liked ? "fill-sakura-400 text-sakura-500 animate-heart-pop" : "text-plum/60 dark:text-cream/60"}`} strokeWidth={1.9} />
              {burst && [0,1,2,3,4,5].map((i) => (
                <span
                  key={i}
                  className="absolute h-1.5 w-1.5 rounded-full bg-sakura-300 animate-particle-out"
                  style={{ "--tx": `${Math.cos((i / 6) * Math.PI * 2) * 30}px`, "--ty": `${Math.sin((i / 6) * Math.PI * 2) * 30}px` }}
                />
              ))}
              {likeCount > 0 && (
                <span className="absolute -bottom-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-sakura-500 text-white text-[9px] font-bold flex items-center justify-center border border-white/40">
                  {likeCount}
                </span>
              )}
            </span>
          </button>

          {/* Beli */}
          <button
            onClick={doIntent}
            disabled={soldOut}
            data-testid={T.productIntent(product.id)}
            className={`flex items-center justify-center gap-2 rounded-full px-10 transition-transform active:scale-[0.97] ${soldOut ? "opacity-50 cursor-not-allowed bg-plum/10 border border-plum/10" : "bg-sakura-400 border border-white/50 shadow-[0_8px_28px_-4px_rgba(245,169,199,0.6)]"}`}
            style={{ height: `${DOCK_H}px` }}
            aria-label="Intent Beli"
          >
            <ShoppingBag className="h-5 w-5 text-plum" strokeWidth={2.2} />
            <span className="text-[13px] font-bold uppercase tracking-[0.18em] text-plum">
              {soldOut ? "Habis" : "Beli"}
            </span>
          </button>

          {/* Chat */}
          <button onClick={() => setShowChat(true)} data-testid={T.productWa(product.id)} aria-label="Chat admin" className="transition-transform active:scale-90">
            <span
              className="grid place-items-center rounded-full bg-white dark:bg-plum/40 border border-plum/10 shadow-dreamy"
              style={{ height: `${DOCK_H}px`, width: `${DOCK_H}px` }}
            >
              <MessageCircle className="h-5 w-5 text-plum/60 dark:text-cream/60" strokeWidth={1.8} />
            </span>
          </button>
        </div>
      </div>

      <ChatAdminSheet product={showChat ? product : null} onClose={() => setShowChat(false)} />
    </div>
  );
}
